import { PageHeading, PromiseMessage, PromiseRetry, Redirect } from 'lib/components';
import { observer } from 'mobx-react';
import React from "react";
import { StudentEditProfileStore } from './StudentEditProfileStore';
import { ReactNodeGenerator } from 'lib/helpers';
import AvatarEditor from 'react-avatar-editor';

const MAX_FILE_SIZE_MB = 2; // Set the maximum file size in MB
const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg', 'image/jpg']; // Allowed file types

function* renderer(store: StudentEditProfileStore): ReactNodeGenerator {
    if (store.redirectTo !== null) {
        yield (<Redirect to={store.redirectTo} key="student-redirect"/>);
        return;
    }

    yield (<PageHeading key="StudentHeading"> Student Profile </PageHeading>);

    const saveState = store.saveState;
    const isSavePending = saveState?.isPending === true;

    yield (
        <>
            <div className="row" key="student-profile">
                <div className="col-sm-6 col-md-6">
                  <div className={store.firstName.formGroupClassName}>
                    <label>First name</label>
                    <input className="form-control" type="text"
                           value={store.firstName.value}
                           onChange={store.firstName.onChangeHandler}
                           onBlur={store.firstName.markTouched}
                           key={"FirstName"}/>
                    <div className="invalid-text">Enter first name</div>
                  </div>
                </div>

                <div className="col-sm-6 col-md-6">
                  <div className={store.lastName.formGroupClassName}>
                    <label>Last name</label>
                    <input className="form-control" type="text"
                           value={store.lastName.value}
                           onChange={store.lastName.onChangeHandler}
                           onBlur={store.lastName.markTouched}
                           key={"LastName"}/>
                    <div className="invalid-text">Enter last name</div>
                  </div>
                </div>                

                <div className="col-sm-6 col-md-6">
                    <div className="mb-3">
                        <label htmlFor="formFileSm" className="form-label">Profile Picture</label>
                        <input style={{height: '35px'}} className="form-control form-control-sm" id="formFileSm" type="file" onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                                const file = e.target.files[0];
                                
                                if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
                                    alert(`File size exceeds ${MAX_FILE_SIZE_MB} MB`);
                                    e.target.value = '';
                                    return;
                                }

                                if (!ALLOWED_FILE_TYPES.includes(file.type)) {
                                    alert('Invalid file type. Only PNG and JPEG are allowed.');
                                    e.target.value = '';
                                    return;
                                }
                                
                                store.setAvatar(file);
                                store.showAvatarEditor = true;
                            }
                        }}/>
                    </div>
                    {store.croppedImage && (
                        <div className="mt-3 mb-3">
                            <img src={store.croppedImage} 
                                alt="Profile picture" 
                                className="img-thumbnail" />
                        </div>
                    )}                    
                </div>
                <div className="col-12 ml-auto">
                    <button type="button" className="btn btn-primary mr-2"
                            onClick={store.save}
                            disabled={isSavePending}> Save
                    </button>
                    <button type="button" className="btn btn-light"
                            onClick={store.cancel}
                            disabled={isSavePending}> Cancel
                    </button>                    
                </div>
            </div>

            <div className={`modal ${store.showAvatarEditor ? 'show' : ''}`} style={{ display: store.showAvatarEditor ? 'block' : 'none' }} aria-modal="true" role="dialog">
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Profile Picture</h5>
                            <button type="button" className="close" aria-label="Close" onClick={() => store.showAvatarEditor = false}>
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div className="modal-body d-flex justify-content-center">
                            <AvatarEditor
                                ref={(editor) => { if (editor) store.editor = editor; }}
                                image={store.avatar || ''}
                                width={250}
                                height={250}
                                border={50}
                                scale={1.2}
                            />
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={() => store.showAvatarEditor = false}>Close</button>
                            <button type="button" className="btn btn-primary" onClick={() => {
                                const editor = store.editor;
                                if (editor) {
                                    // Get the cropped image from the AvatarEditor
                                    const canvas = editor.getImageScaledToCanvas();

                                    // Resize the image to 100x100 using another canvas
                                    const resizedCanvas = document.createElement('canvas');
                                    resizedCanvas.width = 100;
                                    resizedCanvas.height = 100;
                                    const ctx = resizedCanvas.getContext('2d');
                                    ctx?.drawImage(canvas, 0, 0, 100, 100);
                                    const imageUrl = resizedCanvas.toDataURL();

                                    resizedCanvas.toBlob((blob) => store.setAvatar(blob!));
                                    store.saveCroppedAvatar(imageUrl);
                                }
                            }}>Save Changes</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    yield (<PromiseMessage key="save-error-student" label="Saving" state={saveState} align="right"/>);
}
  
interface Props {
    id: number;
}

@observer
export class StudentProfile extends React.Component<Props> {
    readonly store: StudentEditProfileStore;

    constructor(props: Readonly<Props>) {
        super(props);
        this.store = new StudentEditProfileStore(props.id);
    }

    componentDidMount() {
        this.store.loadUser();
    }

    render() {
        return Array.from(renderer(this.store));
    }

    componentWillUnmount() {
        this.store.dispose();
    }
}
